import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { MaskedLine } from "@/components/Motion";
import { scrollToSection } from "@/components/Navbar";

const HERO_IMG = "https://images.unsplash.com/photo-1564981797816-1043664bf78d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwbGlicmFyeXxlbnwwfHx8fDE3ODgxMTAyOTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const parX = useSpring(mx, { stiffness: 50, damping: 20 });
  const parY = useSpring(my, { stiffness: 50, damping: 20 });

  const onMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 36);
    my.set((e.clientY / window.innerHeight - 0.5) * 24);
  };

  return (
    <section ref={ref} id="top" onMouseMove={onMouseMove} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0 -z-10" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="Library interior" className="w-full h-[120%] object-cover saturate-[1.2] brightness-110" />
        <div className="absolute inset-0 bg-[#031435]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#031435] via-transparent to-[#031435]/40" />
      </motion.div>
      <motion.div
        animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -left-40 top-1/3 w-[55vw] h-[55vw] max-w-[800px] max-h-[800px] rounded-full bg-[#1D5FD6]/20 blur-[130px] pointer-events-none -z-10"
        aria-hidden="true"
      />
      <div className="absolute right-[-10%] top-[-15%] w-[40vw] h-[40vw] max-w-[600px] max-h-[600px] rounded-full bg-[#0268E6]/15 blur-[120px] pointer-events-none -z-10" aria-hidden="true" />

      <motion.div style={{ x: parX, y: parY }} className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {[
          { w: "Hallo", x: "64%", y: "16%", s: "text-6xl lg:text-8xl", d: 0 },
          { w: "Bonjour", x: "76%", y: "52%", s: "text-5xl lg:text-7xl", d: 1.2 },
          { w: "안녕하세요", x: "62%", y: "60%", s: "text-5xl lg:text-7xl", d: 2.1 },
          { w: "Danke", x: "86%", y: "32%", s: "text-4xl lg:text-6xl", d: 0.6 },
          { w: "Merci", x: "42%", y: "8%", s: "text-4xl lg:text-5xl", d: 1.7, c: "text-[#C44900]/30" },
          { w: "Guten Tag", x: "6%", y: "22%", s: "text-5xl lg:text-7xl", d: 0.9 },
          { w: "Salut", x: "22%", y: "10%", s: "text-4xl lg:text-6xl", d: 2.5 },
          { w: "감사합니다", x: "4%", y: "44%", s: "text-3xl lg:text-5xl", d: 1.4 },
          { w: "Au revoir", x: "88%", y: "72%", s: "text-3xl lg:text-4xl", d: 2.8 },
        ].map((f) => (
          <motion.span
            key={f.w}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, -16, 0] }}
            transition={{
              opacity: { delay: 2.6 + f.d * 0.2, duration: 1.4 },
              y: { delay: 2.6 + f.d * 0.2, duration: 7 + f.d, repeat: Infinity, ease: "easeInOut" },
            }}
            className={`absolute font-display italic ${f.c || "text-white/[0.2]"} ${f.s}`}
            style={{ left: f.x, top: f.y }}
          >
            {f.w}
          </motion.span>
        ))}
      </motion.div>

      <motion.div style={{ opacity: fade }} className="h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24 lg:pb-28">
        <MaskedLine delay={0.35} className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#C44900] mb-6">
          <span data-testid="hero-eyebrow">Edvio Language Institute — Urwa, Mangalore</span>
        </MaskedLine>

        <h1 className="font-display text-[clamp(3rem,10vw,8.5rem)] leading-[0.95] tracking-tighter text-[#F4F4F0]">
          <MaskedLine delay={0.5}><span data-testid="hero-line-1">Speak the</span></MaskedLine>
          <MaskedLine delay={0.65}>
            <span data-testid="hero-line-2">
              <em className="italic font-medium text-[#C44900]">world's</em> language.
            </span>
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <MaskedLine delay={0.85} className="max-w-md text-sm sm:text-base text-[#A3A3A3] leading-relaxed">
            <span data-testid="hero-subtext">
              German, French &amp; Korean taught in small batches — from first words to
              Goethe, DELF and TOPIK certification.
            </span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#courses")}
              className="group flex items-center gap-3 bg-[#F4F4F0] text-[#0A0B0A] text-xs uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-[#C44900] hover:text-[#F4F4F0] transition-colors duration-300"
              data-testid="hero-explore-courses-button"
            >
              Explore Courses
            </button>
            <div className="hidden sm:flex items-center gap-2 text-xs text-[#A3A3A3]" data-testid="hero-rating">
              <Star size={14} className="fill-[#C44900] text-[#C44900]" />
              5.0 rated by students
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        onClick={() => scrollToSection("#courses")}
        className="absolute bottom-6 right-6 lg:right-12 text-[#A3A3A3] hover:text-[#F4F4F0] transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={20} />
        </motion.div>
      </motion.button>
    </section>
  );
}
